import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { assert } from '@ember/debug';
import TaskService from 'donor-elf-web/services/task-service';
import JournalService from 'donor-elf-web/services/journal-service';
import V2TaskModel from 'donor-elf-web/models/v2-task';

export type onTaskSavingArgs = { saving: boolean; task: V2TaskModel };
export type onTaskSavingCallback = (args: onTaskSavingArgs) => void;

export type onTaskSaveErrorArgs = { task: V2TaskModel; error: any };
export type onTaskSaveErrorCallback = (args: onTaskSaveErrorArgs) => void;

export type onTaskSavedOptionsArgs = { addAnother?: boolean };
export type onTaskSavedCallback = (
  task: V2TaskModel,
  options?: onTaskSavedOptionsArgs,
) => void;
export type onTaskDeletedCallback = (task: V2TaskModel) => void;
export type onCancelEditTaskCallback = (task: V2TaskModel) => void;

export type onCopyTaskArgs = { task: V2TaskModel; pasteAll: boolean };
export type onCopyTaskCallback = (args: onCopyTaskArgs) => void;

interface ComponentArgs {
  task: V2TaskModel;
  changeset?: any;
  displayJournalInfo?: boolean; // default true
  onlyEdit?: boolean; // default false
  hideSaveAndCancel?: boolean; // default false
  showCopyIcon?: boolean;
  copyAndPasteAllConfirmation?: string; // if present, it will show paste option with this confirmation message
  saveImmediately?: boolean; //if true it saves the task immediately
  inAddModal?: boolean; // set to true if this is in the global Add modal
  hideAddAnother?: boolean; // hide the 'Save & Add Another' button while editing the task
  onCopyTask?: onCopyTaskCallback;
  onTaskSaving?: onTaskSavingCallback;
  onTaskSaveError: onTaskSaveErrorCallback;
  onTaskSaved: onTaskSavedCallback;
  onTaskDeleted: onTaskDeletedCallback;
  onCancelEditTask?: onCancelEditTaskCallback;
}

export default class TaskInfoIndexComponet extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);

    assert(
      '@onTaskDeleted() argument must be present for Task::Info',
      typeof args.onTaskDeleted === 'function',
    );
  }

  @service declare taskService: TaskService;
  @service declare journalService: JournalService;

  @tracked _editing = false;

  get displayJournalInfo() {
    return this.args.displayJournalInfo ?? true;
  }

  get editing() {
    return this.args.onlyEdit || this._editing || this.args.task.isNew;
  }

  @action
  handleEditTask() {
    this._editing = true;
  }

  @action
  handleTaskSaved(task: V2TaskModel, options?: onTaskSavedOptionsArgs) {
    this._editing = false;
    this.args.onTaskSaved?.(task, options);
  }

  @action
  handleEditCancelled() {
    this._editing = false;
    this.args.onCancelEditTask?.(this.args.task);
  }

  @action
  handleTaskDeleted(task: V2TaskModel) {
    this._editing = false;
    this.args.onTaskDeleted(task);
  }
}
